import { useState, useEffect } from 'react'
import { Button } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import ChatLayout from '@/Layout/ChatLayout'
import Reports from '@/pages/PoolPage/stockDetail/reports'
import AIChat from '@/pages/PoolPage/stockDetail/components/AIChat'
import StockAI from '@/pages/PoolPage/stockDetail/components/StockAI'
import OverviewTab from './tabs/OverviewTab'
import FinancialsTab from './tabs/FinancialsTab'
import InstitutionalTab from './tabs/InstitutionalTab'
import AIResearchTab from './tabs/AIResearchTab'
import CompanyTab from './tabs/CompanyTab'
import FundTab from './tabs/FundTab'
import SkillPanel from './components/SkillPanel'
import { getStockFinancedetail, getStockBaseMetrics } from '@/api'
import type { StockReport, StockBaseMetricsResponse, StockFinancialData } from '@/types/stockPool'
import '@/pages/PoolPage/stockDetail/index.scss'
import './index.scss'

const TABS = [
    { id: 1, title: '概况' },
    { id: 2, title: '财务数据' },
    { id: 3, title: '研究文档' },
    { id: 4, title: '机构与估值' },
    { id: 5, title: '资金与股东' },
    { id: 6, title: '公司与事件' },
    { id: 7, title: 'AI 研究' },
]

interface Props {
    code?: string
    onBack?: () => void
}

export default function StockNewKBDetailPage({ code: codeProp, onBack }: Props) {
    const { code: codeParam } = useParams<{ code: string }>()
    const navigate = useNavigate()
    const stockCode = codeProp ?? codeParam ?? ''
    const handleBack = onBack ?? (() => navigate(-1))

    const [activeKey, setActiveKey] = useState(1)
    const [stockName, setStockName] = useState<string | null>(null)
    const [baseMetrics, setBaseMetrics] = useState<StockBaseMetricsResponse | null>(null)
    const [financials, setFinancials] = useState<StockFinancialData[]>([])
    const [report, setReport] = useState<StockReport | null>(null)

    useEffect(() => {
        if (!stockCode) return
        // 切换股票时立即清空旧数据，防止旧数据残留展示
        setStockName(null)
        setFinancials([])
        setBaseMetrics(null)
        getStockFinancedetail(stockCode)
            .then(data => {
                setStockName(data.name ?? null)
                setFinancials(data.items ?? [])
            })
            .catch(() => {})
        getStockBaseMetrics(stockCode)
            .then(data => { if (data) setBaseMetrics(data) })
            .catch(() => {})
    }, [stockCode])

    const metrics = baseMetrics?.metrics ?? null
    const latestFinancial = financials[0] ?? null

    const tabContent: Record<number, React.ReactNode> = {
        1: <OverviewTab stockCode={stockCode} metrics={metrics} latestFinancial={latestFinancial} />,
        2: <FinancialsTab stockCode={stockCode} financials={financials} />,
        3: <Reports
            stockCode={stockCode}
            onReportClick={setReport}
            renderFooter={(r) => r ? <SkillPanel reportId={r.id} reportType={r.report_type} /> : null}
        />,
        4: <InstitutionalTab stockCode={stockCode} />,
        5: <FundTab stockCode={stockCode} />,
        6: <CompanyTab stockCode={stockCode} />,
        7: <AIResearchTab financials={financials} />,
    }

    const sider = activeKey === 3
        ? <StockAI code={stockCode} report={report ?? null} />
        : activeKey === 7
        ? <StockAI code={stockCode} report={null} />
        : <AIChat />

    return (
        <ChatLayout
            className="stock-detail-page page-container"
            headerClassName="stock-detail-page__header"
            header={
                <div className="stock-detail-page__header-content">
                    <div className="text-12 flex items-center gap-8 stock-detail-page__header-title">
                        <Button icon={<LeftOutlined />} style={{ marginLeft: 0 }} size="small" onClick={handleBack} />
                        <span style={{ fontSize: 15, fontWeight: 700 }}>{stockName || stockCode}</span>
                        <span className="color-3">{stockCode}</span>
                    </div>
                    <div className="stock-detail-page__header-knowledge-base flex items-center gap-8">
                        {TABS.map(tab => (
                            <div
                                key={tab.id}
                                className={`stock-detail-page__header-content-item flex items-center gap-4 flex-between ${tab.id === activeKey ? 'is-active' : ''}`}
                                onClick={() => setActiveKey(tab.id)}
                            >
                                <div className="stock-detail-page__aside-content-item-title">{tab.title}</div>
                            </div>
                        ))}
                    </div>
                </div>
            }
            contentClassName="stock-detail-page__content"
            content={tabContent[activeKey]}
            sider={sider}
            siderWidth={560}
            resizableSider
            minSiderWidth={380}
            maxSiderWidth={920}
        />
    )
}
